import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowRight, ArrowUpRight } from "lucide-react";
import { motion, useScroll, useSpring } from "framer-motion";
import { NAV_LINKS } from "@/data/content";

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { scrollYProgress } = useScroll();
  const progress = useSpring(scrollYProgress, { stiffness: 120, damping: 26, mass: 0.4 });

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname, location.hash]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const darkHero = location.pathname === "/entrade" && !scrolled && !open;
  const linkCls = darkHero ? "text-[#A0A09C] hover:text-ivory" : "text-dim hover:text-ink";

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-ivory/80 backdrop-blur-xl border-b border-line" : "bg-transparent border-b border-transparent"
      } ${darkHero ? "text-ivory" : "text-ink"}`}
    >
      <motion.div
        className="absolute top-0 left-0 right-0 h-[2px] bg-pine origin-left z-10"
        style={{ scaleX: progress }}
        aria-hidden="true"
      />
      <div className="container-x flex items-center justify-between h-16 lg:h-[72px]">
        <Link to="/" data-testid="nav-logo" className="flex items-center select-none" aria-label="T Plus One home">
          <span className={`inline-flex items-center rounded-md transition-colors duration-500 ${darkHero ? "bg-ivory px-3 py-1.5" : ""}`}>
            <img src="/tpo-logo.png" alt="T Plus One" className="h-10 lg:h-11 w-auto" />
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-8" aria-label="Primary">
          {NAV_LINKS.map((l) =>
            l.to.includes("#") ? (
              <Link key={l.to} to={l.to} data-testid={l.testid} className={`nav-link text-sm transition-colors ${linkCls}`}>
                {l.label}
              </Link>
            ) : (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={l.testid}
                className={({ isActive }) => `nav-link text-sm transition-colors ${isActive ? "active " + (darkHero ? "text-ivory" : "text-ink") : linkCls}`}
              >
                {l.label}
              </NavLink>
            )
          )}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <Link
            to="/courses"
            data-testid="nav-cta-explore-courses"
            className={`group inline-flex items-center gap-2 rounded-full text-sm font-medium px-5 py-2.5 transition-colors ${
              darkHero ? "bg-ivory text-ink hover:bg-pine hover:text-ivory" : "bg-ink text-ivory hover:bg-pine"
            }`}
          >
            Explore Courses
            <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5" />
          </Link>
          <Link
            to="/entrade"
            data-testid="nav-cta-join-entrade"
            className={`group inline-flex items-center gap-1.5 text-sm font-medium transition-colors px-2 py-2.5 ${
              darkHero ? "text-ivory hover:text-pine-2" : "text-ink hover:text-pine"
            }`}
          >
            Join EnTrade
            <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          className="lg:hidden p-2 -mr-2"
          onClick={() => setOpen(!open)}
          aria-expanded={open}
          aria-label={open ? "Close menu" : "Open menu"}
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <div
        data-testid="mobile-menu"
        className={`lg:hidden fixed inset-0 top-16 bg-ivory transition-all duration-300 ${
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      >
        <nav className="container-x pt-10 flex flex-col gap-2" aria-label="Mobile">
          {NAV_LINKS.map((l, i) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`mobile-${l.testid}`}
              onClick={() => setOpen(false)}
              className={`py-4 text-2xl tracking-tight border-b border-line transition-all duration-500 ${open ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`}
              style={{ transitionDelay: `${i * 50}ms` }}
            >
              {l.label}
            </Link>
          ))}
          <div className="flex flex-col gap-3 pt-8">
            <Link to="/courses" data-testid="mobile-cta-explore-courses" className="inline-flex items-center justify-center gap-2 rounded-full bg-ink text-ivory font-medium px-6 py-4">
              Explore Courses <ArrowRight size={16} />
            </Link>
            <Link to="/entrade" data-testid="mobile-cta-join-entrade" className="inline-flex items-center justify-center gap-2 rounded-full border border-ink/20 font-medium px-6 py-4">
              Join EnTrade <ArrowUpRight size={16} />
            </Link>
          </div>
        </nav>
      </div>
    </header>
  );
};

export default Navbar;
